import { useEffect, useState } from 'react'

/**
 * `queryOptions` for the record query of an edit form. Every refetch hands
 * out a new record object (its `Date`s defeat structural sharing), and Refine
 * then writes the record over every field, so a background refetch (window
 * focus, reconnect) would wipe what the user typed. The record still loads
 * fresh on mount.
 */
export const FORM_RECORD_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const

type RecordQuery = { data?: unknown; isFetching: boolean } | undefined

/**
 * Edit mode: true until the record query has settled and its data is in the
 * form (`appliedData` is the data the form last took over).
 */
export function isRecordPending(
  action: 'create' | 'edit',
  query: RecordQuery,
  appliedData: unknown,
) {
  return (
    action === 'edit' &&
    (query?.data === undefined ||
      query.isFetching ||
      appliedData !== query.data)
  )
}

/**
 * Keeps an edit form read-only until its record has settled. Opened with the
 * record already cached (Show → Edit), the form shows the cached record while
 * it refetches on mount, and Refine's `useForm` writes the fetched record over
 * every field when it lands, so anything typed before would be lost. Call it
 * after `useForm`: Refine applies the record from an effect (in a microtask),
 * so this effect, running after Refine's, marks the data as applied only once
 * Refine has queued the write, and the form unlocks after it.
 */
export function useRecordPending(
  action: 'create' | 'edit',
  query: RecordQuery,
) {
  const [appliedData, setAppliedData] = useState<unknown>(undefined)
  const data = query?.data
  const isFetching = query?.isFetching ?? false
  useEffect(() => {
    if (!isFetching) {
      setAppliedData(data)
    }
  }, [data, isFetching])
  return isRecordPending(action, query, appliedData)
}
